import { useState } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { PiArrowBendUpLeft, PiCaretDown, PiCaretUp } from 'react-icons/pi'
import { SafeEmailRenderer } from './SafeEmailRenderer'
import { Attachment } from 'components/UI/Attachment/Attachment'
import { cn } from 'components/UI/cn'
import { EmailContact, EmailMessage } from 'models/emailIntegration'
import { ComposeMode } from './ComposePanel'
import { ThreadMessageMenu } from './ThreadMessageMenu'

/** Extract initials from a name string. */
function getInitials(name: string): string {
  return (
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'UN'
  )
}

/** Format a date as "9:14 AM (8 hours ago)". */
function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr)
  const absolute = format(date, 'h:mm a')
  const relative = formatDistanceToNow(date, { addSuffix: true })
  return `${absolute} (${relative})`
}

/** Summarize recipients as "to me, Sidney, Sharon". */
function summarizeRecipients(recipients: EmailContact[]): string {
  return recipients.map((r) => r.name || r.email).join(', ')
}

/** Format a contact as "Name <email>" or just "email". */
function formatContact(contact: EmailContact): string {
  return contact.name ? `${contact.name} <${contact.email}>` : contact.email
}

interface ThreadMessageProps {
  message: EmailMessage
  isLast: boolean
  onCompose?: (mode: ComposeMode, message: EmailMessage) => void
}

/** A single message card within the thread view. */
export function ThreadMessage({
  message,
  isLast,
  onCompose,
}: ThreadMessageProps) {
  const [recipientsExpanded, setRecipientsExpanded] = useState(false)

  const senderInfo = message.sender?.[0]
  const senderName = senderInfo?.name || 'Unknown'
  const senderEmail = senderInfo?.email || ''
  const toRecipients = message.to || []
  const ccRecipients = message.cc || []
  const nonInlineAttachments = (message.attachments || []).filter(
    (a) => !a.is_inline
  )

  return (
    <div className={cn('px-6 py-5', !isLast && 'border-b border-[#EDEFF1]')}>
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-[#C4C4C4] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">
            {getInitials(senderName)}
          </span>
        </div>

        {/* Sender info + recipients */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-[#191D23] truncate">
              {senderName}
            </span>
            {senderEmail && (
              <span className="text-sm text-[#6E6E6E] truncate">
                &lt;{senderEmail}&gt;
              </span>
            )}

            {/* Right side: timestamp + actions */}
            <div className="ml-auto flex items-center gap-1 flex-shrink-0">
              <span className="text-xs text-[#6E6E6E]">
                {formatTimestamp(message.date)}
              </span>
              <button
                type="button"
                className="p-1 rounded hover:bg-[#F3F3F3] text-[#6E6E6E] transition-colors"
                title="Reply"
                disabled={!onCompose}
                onClick={() => onCompose?.('reply', message)}
              >
                <PiArrowBendUpLeft className="size-4" />
              </button>
              <ThreadMessageMenu
                onCompose={
                  onCompose ? (mode) => onCompose(mode, message) : undefined
                }
              />
            </div>
          </div>

          {/* Recipients line */}
          {toRecipients.length > 0 && (
            <button
              type="button"
              onClick={() => setRecipientsExpanded((prev) => !prev)}
              className="flex items-center gap-0.5 text-sm text-[#6E6E6E] hover:text-[#191D23] transition-colors mt-0.5"
            >
              <span className="truncate max-w-[300px]">
                to {summarizeRecipients(toRecipients)}
              </span>
              {recipientsExpanded ? (
                <PiCaretUp className="size-3 flex-shrink-0" />
              ) : (
                <PiCaretDown className="size-3 flex-shrink-0" />
              )}
            </button>
          )}

          {/* Expanded recipient details */}
          {recipientsExpanded && (
            <div className="mt-2 text-xs text-[#6E6E6E] space-y-1">
              {toRecipients.length > 0 && (
                <div>
                  <span className="font-medium">To: </span>
                  {toRecipients.map(formatContact).join(', ')}
                </div>
              )}
              {ccRecipients.length > 0 && (
                <div>
                  <span className="font-medium">Cc: </span>
                  {ccRecipients.map(formatContact).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Message body */}
      <div className="mt-4 pl-[52px] text-sm leading-relaxed">
        <SafeEmailRenderer html={message.body || ''} />
      </div>

      {/* Attachments */}
      {nonInlineAttachments.length > 0 && (
        <div className="mt-3 pl-[52px] flex flex-wrap gap-2">
          {nonInlineAttachments.map((attachment) => (
            <Attachment
              key={attachment.attachment_id}
              filename={attachment.filename}
              size={attachment.size}
              contentType={attachment.content_type}
            />
          ))}
        </div>
      )}
    </div>
  )
}
